import React from "react";
import Head from "next/head";
import Link from "next/link";
import { UniversityOverlay } from "@/components/university/UniversityOverlay";
import {
    ArrowLeft,
    ArrowRight,
    BookOpen,
    Shield,
    Scale,
    Gavel,
    PenTool,
    CheckCircle2,
    AlertCircle,
    Clock,
    ExternalLink,
} from "lucide-react";
import { useAuth } from "@/lib/supabase/AuthContext";

export default function IRACLegalWritingArticle() {
    const { user } = useAuth();
    const assignmentHref = user ? "/assignments" : "/signup?next=/assignments";

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "What does IRAC stand for in law?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "IRAC stands for Issue, Rule, Application, and Conclusion. It is a fundamental structural framework used to organise legal analysis, ensuring that the legal question is clear, the relevant law is stated accurately, applied to the facts, and a logical conclusion is reached."
                }
            },
            {
                "@type": "Question",
                "name": "Is IRAC used in UK law schools?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "IRAC is a widely taught framework for answering law problem questions, but terminology and expectations vary by university and module. Follow your own module guidance where it differs."
                }
            },
            {
                "@type": "Question",
                "name": "What is the difference between IRAC and CREAC?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "IRAC starts with the 'Issue', whereas CREAC starts with a 'Conclusion' (or 'Contention'). CREAC is common in professional practice where senior partners or clients want the answer first, while IRAC is typically preferred in academic settings to show the full path of reasoning."
                }
            },
            {
                "@type": "Question",
                "name": "When should I use IRAC in exams?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "IRAC should be used for problem questions involving factual disputes and in timed exams where structural clarity is vital. It is especially useful for modules like Contract, Tort, and Criminal Law where multiple legal tests must be applied to specific scenarios."
                }
            },
            {
                "@type": "Question",
                "name": "Can IRAC be used in essay questions?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "While elements of IRAC support clarity in complex arguments, it is less suited for purely theoretical or jurisprudential essays that prioritise critique over structured application. For theoretical work, consider focusing more on the 'Rule' (critique) and 'Explanation' than strict factual application."
                }
            }
        ]
    };

    return (
        <div className="bg-white dark:bg-[#0B1412] min-h-screen flex flex-col transition-colors duration-500">
            <Head>
                <title>IRAC Method for UK Law Problem Questions | Durmah</title>
                <meta
                    name="description"
                    content="Learn the IRAC method for UK law problem questions: spot issues, state supported rules, apply them to the facts and reach reasoned conclusions."
                />
                <link key="route-canonical"
                    rel="canonical"
                    href="https://www.durmah.ai/insights/irac-legal-writing"
                />
                <meta property="og:title" content="IRAC Method for UK Law Problem Questions | Durmah" />
                <meta property="og:description" content="Learn the IRAC method for UK law problem questions: spot issues, state supported rules, apply them to the facts and reach reasoned conclusions." />
                <meta property="og:type" content="article" />
                <meta key="route-og-url" property="og:url" content="https://www.durmah.ai/insights/irac-legal-writing" />
                <meta property="og:image" content="https://www.durmah.ai/og-image.png?v=2" />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
                />
            </Head>

            <main className="flex-1">
                <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
                    <Link
                        href="/learn/exam-technique"
                        prefetch={false}
                        className="inline-flex items-center gap-2 text-sm text-[#123733] dark:text-[#D5BF76] font-black mb-8 hover:underline"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Guides
                    </Link>

                    <div className="prose prose-indigo dark:prose-invert max-w-none">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D5BF76]/20 text-[#D5BF76] text-[10px] font-black uppercase tracking-widest border border-[#D5BF76]/30 mb-6 font-sans">
                            <PenTool className="w-3.5 h-3.5" />
                            <span>Writing Guide</span>
                        </div>

                        <h1 className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white mb-6 tracking-tight leading-tight font-sans">
                            The IRAC Method: <br />
                            <span className="text-[#D5BF76]">
                                Mastering Legal Writing Structure
                            </span>
                        </h1>

                        <p className="text-xl text-gray-600 dark:text-teal-100/70 leading-relaxed mb-6 font-medium">
                            Knowing the law is only part of a problem-question answer. You also need to show how the law applies to the facts. <strong>IRAC stands for Issue, Rule, Application, and Conclusion.</strong> It is a widely used framework for structuring that analysis, although your own module guidance should always take priority.
                        </p>

                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
                            When Should You Use IRAC?
                        </h2>

                        {/* University-specific guidance (overlay), driven by active pack */}
                        <UniversityOverlay slug="guides/irac" />
                        <ul className="space-y-3 mb-8 text-lg text-gray-700 dark:text-gray-300">
                            <li className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-[#D5BF76] shrink-0 mt-0.5" />
                                <span>Problem questions involving factual disputes</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-[#D5BF76] shrink-0 mt-0.5" />
                                <span>Timed law school exams</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-[#D5BF76] shrink-0 mt-0.5" />
                                <span>Coursework requiring structured legal reasoning</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-[#D5BF76] shrink-0 mt-0.5" />
                                <span>Case analysis exercises</span>
                            </li>
                        </ul>
                        <p className="text-lg leading-relaxed mb-12 text-gray-700 dark:text-gray-300 italic">
                            IRAC is less suitable for purely theoretical or jurisprudential essays that prioritise critique over structured application. However, elements of IRAC may still support clarity in complex arguments.
                        </p>

                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
                            Why IRAC Matters
                        </h2>
                        <p className="text-lg leading-relaxed mb-8 text-gray-700 dark:text-gray-300">
                            Legal problems can be overwhelming. IRAC provides a cognitive anchor, ensuring you never miss a step in your reasoning. It mirrors the exact logic examiners look for: identifying the conflict, stating the law accurately, showing how that law interacts with the facts, and providing a definitive outcome.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
                            <div className="bg-emerald-50/50 dark:bg-emerald-500/5 p-8 rounded-[2rem] border border-emerald-100 dark:border-emerald-500/10">
                                <h3 className="text-emerald-700 dark:text-emerald-400 font-black mb-4 flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5" />
                                    The DOs
                                </h3>
                                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                                    <li>Keep the <strong>Issue</strong> concise and focused.</li>
                                    <li>Synthesise <strong>Rules</strong> into a logical flow.</li>
                                    <li>Give most of your analytical space to <strong>Application</strong>.</li>
                                    <li>Signpost clearly using headings.</li>
                                </ul>
                            </div>
                            <div className="bg-rose-50/50 dark:bg-rose-500/5 p-8 rounded-[2rem] border border-rose-100 dark:border-rose-500/10">
                                <h3 className="text-rose-700 dark:text-rose-400 font-black mb-4 flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5" />
                                    The DON'Ts
                                </h3>
                                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                                    <li>Don't "rule dump" irrelevant cases.</li>
                                    <li>Don't re-state the facts without analysis.</li>
                                    <li>Don't hedge your <strong>Conclusion</strong> too much.</li>
                                    <li>Don't forget to cite your authorities.</li>
                                </ul>
                            </div>
                        </div>

                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 font-sans">
                            The IRAC Breakdown
                        </h2>
                        <div className="space-y-8">
                            <section>
                                <h4 className="text-xl font-bold text-[#D5BF76]">1. Issue (I)</h4>
                                <p className="text-gray-700 dark:text-gray-300">Identify the specific legal question that must be resolved. Frame it as a question: <em>"Did X's actions amount to negligence under the duty of care?"</em></p>
                            </section>
                            <section>
                                <h4 className="text-xl font-bold text-[#D5BF76]">2. Rule (R)</h4>
                                <p className="text-gray-700 dark:text-gray-300">State the relevant legal principles, statutes, or case law. This is a synthesis of the law that applies to the issue, not a bare list.</p>
                            </section>
                            <section>
                                <h4 className="text-xl font-bold text-[#D5BF76]">3. Application (A)</h4>
                                <p className="text-gray-700 dark:text-gray-300">The most critical part. Connect the Rule to the Facts. Use connective words like "Because," "Therefore," and "Accordingly" to show your reasoning.</p>
                            </section>
                            <section>
                                <h4 className="text-xl font-bold text-[#D5BF76]">4. Conclusion (C)</h4>
                                <p className="text-gray-700 dark:text-gray-300">Provide a definitive answer to the Issue based on your Application. Even if the law is uncertain, give a probable outcome.</p>
                            </section>
                        </div>

                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mt-16 mb-6 font-sans">
                            Mini Worked Example
                        </h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-6">
                            This is an illustration of structure, not a statement of the law. Check the relevant authorities and your module materials before using any legal proposition.
                        </p>
                        <div className="bg-gray-50 dark:bg-white/5 p-8 rounded-3xl border border-gray-100 dark:border-white/10 italic">
                            <p className="mb-4"><strong>Issue:</strong> Has A committed a breach of contract by delivering the goods two days late?</p>
                            <p className="mb-4"><strong>Rule:</strong> Identify the relevant contractual term and the authorities supplied by your module materials. Explain the legal effect of the timing obligation before applying it.</p>
                            <p className="mb-4"><strong>Application:</strong> Here, Clause 4 of A's contract stated "delivery is required by June 1st." By delivering on June 3rd, A failed to meet a strict deadline agreed upon by both parties. However, if time was not expressly stated to be of the essence and the delay caused no measurable commercial loss, a court might consider whether the breach was sufficiently serious to justify remedies.</p>
                            <p><strong>Conclusion:</strong> Therefore, A is likely in breach of contract.</p>
                        </div>

                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mt-16 mb-6 font-sans">
                            Variants: CREAC and CRAC
                        </h2>
                        <p className="text-lg leading-relaxed mb-8 text-gray-700 dark:text-gray-300">
                            In professional practice, you might encounter <strong>CREAC</strong> (Conclusion, Rule, Explanation, Application, Conclusion). For university problem questions, use the framework your module teaches. Our fuller guide explains how to <Link href="/resources/answering-problem-questions" className="text-[#D5BF76] hover:underline font-bold">answer law problem questions issue by issue</Link>. Once your structure is drafted, it may also help to seek <Link href="/learn/law-essay-feedback-before-submitting" className="text-[#D5BF76] hover:underline font-bold">feedback before you submit</Link>.
                        </p>

                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mt-16 mb-6 font-sans text-rose-600 dark:text-rose-400">
                            A Note on Academic Integrity
                        </h2>
                        <div className="p-8 bg-rose-50/50 dark:bg-rose-500/5 rounded-3xl border border-rose-100 dark:border-rose-500/10 mb-12">
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                Durmah is a learning companion, not a ghostwriter. Use our tools to help you identify issues, plan your IRAC structure, and understand complex rules. Never submit AI-generated text as your own work.
                            </p>
                            <Link href="/legal/academic-integrity" className="inline-flex items-center gap-2 text-rose-600 dark:text-rose-400 font-bold mt-4 hover:underline">
                                Read our Academic Integrity Policy <ExternalLink className="w-4 h-4" />
                            </Link>
                        </div>

                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mt-16 mb-8 font-sans">
                            Frequently Asked Questions About IRAC
                        </h2>
                        <div className="space-y-4 mb-20 not-prose">
                            {faqSchema.mainEntity.map((item, i) => (
                                <details
                                    key={i}
                                    className="group p-6 bg-gray-50 dark:bg-white/[0.02] rounded-3xl border border-gray-100 dark:border-white/[0.05] transition-all hover:border-[#D5BF76]/30 shadow-sm"
                                >
                                    <summary className="text-lg font-bold text-gray-900 dark:text-white cursor-pointer list-none flex items-center justify-between">
                                        {item.name}
                                        <span className="text-[#D5BF76] group-open:rotate-45 transition-transform text-2xl font-light">
                                            +
                                        </span>
                                    </summary>
                                    <p className="mt-4 text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
                                        {item.acceptedAnswer.text}
                                    </p>
                                </details>
                            ))}
                        </div>

                        <div className="not-prose my-16 p-8 md:p-12 bg-[#123733] rounded-[2.5rem] border border-[#D5BF76]/30 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D5BF76]/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-3 text-[#D5BF76] mb-6 font-sans">
                                    <Gavel className="w-8 h-8" />
                                    <span className="text-xs font-black uppercase tracking-[0.2em]">
                                        The IRAC Wizard
                                    </span>
                                </div>

                                <h2 className="text-3xl md:text-4xl font-black text-white mb-8 tracking-tight font-sans">
                                    Struggling with Structure?
                                </h2>

                                <p className="text-teal-50/90 text-lg leading-relaxed mb-10">
                                    Our <Link href="/assignments" className="text-[#D5BF76] font-bold hover:underline">Assignment Assistant</Link> helps you break down a brief and organise your own reasoning into a clearer plan. Your module guidance and your own judgment remain in charge.
                                </p>

                                <Link href={assignmentHref} className="group block">
                                    <div className="flex items-center justify-between p-8 rounded-3xl bg-white/10 border border-white/20 hover:bg-white/20 transition-all">
                                        <span className="text-xl font-black text-white">Try the Assignment Assistant</span>
                                        <ArrowRight className="w-8 h-8 text-[#D5BF76] group-hover:translate-x-2 transition-transform" />
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </article>
            </main>
        </div>
    );
}
